import { ReviewPost } from '../types/review';

type Faq = NonNullable<ReviewPost['faqs']>[number];

const cancel =
  'Cancel in the app’s own account or billing settings before the next renewal. Heaven Girlfriend does not bill you and cannot cancel the plan for you.';

const safety = (name: string) =>
  `${name} is a separate company. Heaven Girlfriend does not run the app or store its chats. Read the app’s privacy policy before you upload a photo or a payment card, and use it only if you are 18 or older.`;

export const HANDOFF_REVIEW_FAQS: Record<string, Faq[]> = {
  'nectar-ai-review': [
    {
      question: 'How much does Nectar AI cost?',
      answer:
        'The price listed on this review is from $4.99 a month on the annual plan. Confirm the live checkout before you pay, because renewal prices can differ from the first term.',
    },
    {
      question: 'Does Nectar AI have a free tier?',
      answer:
        'You can sign up and look around for free, but the free trial does not open the main storytelling chats. Paid access is what this test used for that feature.',
    },
    {
      question: 'Is Nectar AI private?',
      answer: safety('Nectar AI'),
    },
    {
      question: 'How do I cancel Nectar AI?',
      answer: cancel,
    },
    {
      question: 'What is a strong alternative to Nectar AI?',
      answer:
        'Candy AI is the top-ranked alternative in this test when you want realistic chat, strong visuals, and a simpler start. Lovescape is the closer match if you want another storytelling-style companion.',
    },
  ],
  'dreamgf-review': [
    {
      question: 'How much does DreamGF cost?',
      answer:
        'The price listed here is from $5.99 a month for the first year, and it renews higher. Check the billing period on the checkout screen before you pay.',
    },
    {
      question: 'Does DreamGF have a free trial?',
      answer:
        'DreamGF has a free trial, but unlimited messages and full character creation sit on a paid membership.',
    },
    {
      question: 'Is DreamGF private?',
      answer: safety('DreamGF'),
    },
    {
      question: 'How do I cancel DreamGF?',
      answer: cancel,
    },
    {
      question: 'What is a strong alternative to DreamGF?',
      answer:
        'Candy AI covers the same chat, image, and character tools at a similar quality in this test. FantasyGF is the closer pick if you want more to try before paying.',
    },
  ],
  'kupid-ai-review': [
    {
      question: 'How much does Kupid AI cost?',
      answer:
        'On the yearly toggle, Pro was $3.44 per week, billed as one annual charge. Weekly billing was much higher, at $12.99 per week for Pro. Confirm the toggle before you pay.',
    },
    {
      question: 'Does Kupid AI have a useful free tier?',
      answer:
        'You can start an account, but image and video generation spend coins, and the free path runs out quickly compared with the paid plans.',
    },
    {
      question: 'Is Kupid AI private?',
      answer: safety('Kupid AI'),
    },
    {
      question: 'How do I cancel Kupid AI?',
      answer: cancel,
    },
    {
      question: 'What is a strong alternative to Kupid AI?',
      answer:
        'Candy AI is the cheaper alternative in this test when you want images, chat, and an easier first session.',
    },
  ],
  'luvr-ai-review': [
    {
      question: 'How much does Luvr AI cost?',
      answer:
        'The price listed here is from $5.99 a month on annual Gold. Confirm the plan length at checkout.',
    },
    {
      question: 'Does Luvr AI have a free tier?',
      answer:
        'Yes. A free account in this test included chat plus 150 tokens, and daily challenges can earn more tokens without paying.',
    },
    {
      question: 'Is Luvr AI private?',
      answer: safety('Luvr AI'),
    },
    {
      question: 'How do I cancel Luvr AI?',
      answer: cancel,
    },
    {
      question: 'What is a strong alternative to Luvr AI?',
      answer:
        'Candy AI is the higher-ranked alternative if you want a more complete image and chat package. HeraHaven is the closer match if long chat is the main goal.',
    },
  ],
  'fantasygf-review': [
    {
      question: 'How much does FantasyGF cost?',
      answer:
        'The price listed here is from $5.83 a month on the annual plan. Confirm the renewal price at checkout.',
    },
    {
      question: 'Does FantasyGF have a free tier?',
      answer:
        'You can open the site and browse, but full images in chat are for paying members.',
    },
    {
      question: 'Is FantasyGF private?',
      answer: safety('FantasyGF'),
    },
    {
      question: 'How do I cancel FantasyGF?',
      answer: cancel,
    },
    {
      question: 'What is a strong alternative to FantasyGF?',
      answer:
        'DreamGF is the closest product in this set. Candy AI is the higher-ranked pick if you want one app for chat and images.',
    },
  ],
  'herahaven-ai-review': [
    {
      question: 'How much does HeraHaven AI cost?',
      answer:
        'The price listed here is from $6.65 a month on the annual plan. Free members in this test received no Lunas, the in-app currency.',
    },
    {
      question: 'Does HeraHaven AI have a free tier?',
      answer:
        'You can register and send some messages, but the lack of starting Lunas limits almost everything past a short chat.',
    },
    {
      question: 'Is HeraHaven AI private?',
      answer: safety('HeraHaven AI'),
    },
    {
      question: 'How do I cancel HeraHaven AI?',
      answer: cancel,
    },
    {
      question: 'What is a strong alternative to HeraHaven AI?',
      answer:
        'Candy AI is the higher-ranked alternative when you want chat plus images without a separate currency blocking the first session.',
    },
  ],
  'gptgirlfriend-review': [
    {
      question: 'How much does GPTGirlfriend cost?',
      answer:
        'The price listed here is from $12 a month on annual Premium. The free tier in this test was limited, and the fuller character library sits on the paid plan.',
    },
    {
      question: 'Does GPTGirlfriend have a free tier?',
      answer:
        'You can browse and try some chats for free. Faster access and the larger part of the library need Premium.',
    },
    {
      question: 'Is GPTGirlfriend private?',
      answer: safety('GPTGirlfriend'),
    },
    {
      question: 'How do I cancel GPTGirlfriend?',
      answer: cancel,
    },
    {
      question: 'What is a strong alternative to GPTGirlfriend?',
      answer:
        'Spicy Chat AI is the closer alternative for a large community roleplay catalog. Candy AI is the higher-ranked pick for a guided companion app.',
    },
  ],
  'uncensy-review': [
    {
      question: 'How much does Uncensy cost?',
      answer:
        'Uncensy sells token packages from $10 to $100. A custom companion needed more than the 3 starting credits, and this test saw a $5 minimum before that builder opened.',
    },
    {
      question: 'Does Uncensy have a free tier?',
      answer:
        'A new account received 3 free credits for chat or a first look at creation. Building a custom companion used more than those credits.',
    },
    {
      question: 'Is Uncensy private?',
      answer: safety('Uncensy'),
    },
    {
      question: 'How do I cancel Uncensy?',
      answer:
        'Token packs are purchases, not a subscription you cancel in the same way as a monthly plan. Do not buy another pack if you want to stop. Heaven Girlfriend does not bill you.',
    },
    {
      question: 'What is a strong alternative to Uncensy?',
      answer:
        'Candy AI is the higher-ranked alternative if you want chat, images, and a published monthly price instead of token packs.',
    },
  ],
  'soulgen-ai-review': [
    {
      question: 'How much does SoulGen AI cost?',
      answer:
        'The price listed here is from $7.58 a month on the annual plan. Confirm the live checkout before you pay.',
    },
    {
      question: 'Does SoulGen AI have a free tier?',
      answer:
        'You can open the generator and see the tools. The pro image features and heavier generation sit on a paid plan.',
    },
    {
      question: 'Is SoulGen AI private?',
      answer:
        'SoulGen AI is a separate company. Only upload a photo of yourself, or of someone who gave clear permission. Heaven Girlfriend does not run the app. Sexual images of a real person without consent are harmful and illegal in many countries.',
    },
    {
      question: 'How do I cancel SoulGen AI?',
      answer: cancel,
    },
    {
      question: 'What is a strong alternative to SoulGen AI?',
      answer:
        'OurDream AI is the higher-ranked image generator in this test. Candy AI is the better pick if chat matters as much as pictures.',
    },
  ],
  'charstar-ai-review': [
    {
      question: 'How much does Charstar AI cost?',
      answer:
        'The price listed here is from $4.99 a month on the annual plan. Confirm the renewal price at checkout.',
    },
    {
      question: 'Does Charstar AI have a free tier?',
      answer:
        'Sign-up is free, but this test received 10 Stars instead of the 50 the site advertised. That free grant ran out before a real session, so the useful chat was on a paid plan. Charstar does not generate AI images.',
    },
    {
      question: 'Is Charstar AI private?',
      answer: safety('Charstar AI'),
    },
    {
      question: 'How do I cancel Charstar AI?',
      answer: cancel,
    },
    {
      question: 'What is a strong alternative to Charstar AI?',
      answer:
        'Nomi AI is the closer alternative if you want a companion that follows a written bio. Candy AI is the higher-ranked all-rounder.',
    },
  ],
  'replika-ai-review': [
    {
      question: 'How much does Replika cost?',
      answer:
        'The price listed here is from $5.83 a month for annual Pro. You can dismiss the subscription screen and keep a limited free chat.',
    },
    {
      question: 'Does Replika have a free tier?',
      answer:
        'Yes, but it is limited. Activities and the store are blocked on the free account. Close the subscription prompt with the X if you do not want to pay yet.',
    },
    {
      question: 'Is Replika private and what age is it for?',
      answer:
        'Replika is rated 12+ on the US App Store. Heaven Girlfriend is for adults 18+, and this review covers Replika as a general chat app. It is a separate company. Read its privacy policy before you share personal details.',
    },
    {
      question: 'How do I cancel Replika?',
      answer: cancel,
    },
    {
      question: 'What is a strong alternative to Replika?',
      answer:
        'Nomi AI is the closer alternative for a companion built from a bio. Candy AI is the higher-ranked pick if you also want images.',
    },
  ],
  'nomi-ai-review': [
    {
      question: 'How much does Nomi AI cost?',
      answer:
        'The price listed here is from $8.33 a month on the annual plan. Confirm the live checkout before you pay.',
    },
    {
      question: 'Does Nomi AI have a free tier?',
      answer:
        'You need an account before the companion tools open. The useful long-term setup in this test was on a paid plan.',
    },
    {
      question: 'Is Nomi AI private?',
      answer: safety('Nomi AI'),
    },
    {
      question: 'How do I cancel Nomi AI?',
      answer: cancel,
    },
    {
      question: 'What is a strong alternative to Nomi AI?',
      answer:
        'Replika is the closer general-chat alternative. Candy AI is the higher-ranked pick if you want images in the same app.',
    },
  ],
  'botify-ai-review': [
    {
      question: 'How much does Botify AI cost?',
      answer:
        'The price listed here is from $29.99 a year. In-chat media in this test cost Energy, and a free account did not have enough Energy to try the animation feature.',
    },
    {
      question: 'Does Botify AI have a free tier?',
      answer:
        'A new account received 50 Energy. That is enough for some chat. Generating media needed more Energy than the free grant.',
    },
    {
      question: 'Is Botify AI private?',
      answer: safety('Botify AI'),
    },
    {
      question: 'How do I cancel Botify AI?',
      answer: cancel,
    },
    {
      question: 'What is a strong alternative to Botify AI?',
      answer:
        'Spicy Chat AI is the closer alternative for a large roleplay catalog. Candy AI is the higher-ranked companion app.',
    },
  ],
};
